import { useState } from "react"
import { Link } from "react-router-dom"
import { useInfiniteQuery } from "@tanstack/react-query"
import { Grid, List, Search, PlayCircle } from "lucide-react"
import clsx from "clsx"
import { api, ApiError, type CatalogResponse, type VideoSummary } from "@/lib/types"
import { useMe } from "@/lib/auth"
import { formatDate, formatDuration } from "@/lib/format"
import { TagChip } from "@lilbillbiscuit/account-ui"

type ViewMode = "grid" | "list"

export function Catalog() {
  const [view, setView] = useState<ViewMode>("grid")
  const [q, setQ] = useState("")
  const [activeTags, setActiveTags] = useState<string[]>([])

  const me = useMe()

  const catalog = useInfiniteQuery<CatalogResponse, ApiError>({
    queryKey: ["catalog", q, activeTags],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api<CatalogResponse>("/api/catalog", {
        query: { q: q || undefined, tag: activeTags, cursor: (pageParam ?? undefined) as string | undefined, limit: 24 },
      }),
    getNextPageParam: (last) => last.nextCursor,
  })

  const items: VideoSummary[] = catalog.data?.pages.flatMap((p) => p.items) ?? []
  const empty = !catalog.isLoading && items.length === 0
  const userTagSlugs = me.data?.tags.map((t) => t.slug) ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Concert catalog</h1>
          <div className="heading-accent mt-2" />
          <p className="mt-3 text-sm text-th-fg/60">
            {me.data?.user
              ? me.data.tags.length > 0
                ? <>Showing concerts your tags grant access to.</>
                : <>You don't have any tags yet. <Link to="/request-access" className="text-th-accent hover:underline">Request access</Link>.</>
              : <>Sign in to see the concerts you have access to.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-th-fg/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles…"
              className="input w-56 pl-8"
            />
          </div>
          <div role="radiogroup" aria-label="View mode" className="flex rounded-lg border border-th-glass/[0.12] bg-th-glass/[0.04] p-0.5">
            <button
              role="radio"
              aria-checked={view === "grid"}
              onClick={() => setView("grid")}
              className={clsx("rounded-md p-1.5 transition-colors", view === "grid" ? "bg-th-glass/[0.1] text-th-fg" : "text-th-fg/50 hover:text-th-fg")}
              aria-label="Grid view"
            ><Grid size={14} /></button>
            <button
              role="radio"
              aria-checked={view === "list"}
              onClick={() => setView("list")}
              className={clsx("rounded-md p-1.5 transition-colors", view === "list" ? "bg-th-glass/[0.1] text-th-fg" : "text-th-fg/50 hover:text-th-fg")}
              aria-label="List view"
            ><List size={14} /></button>
          </div>
        </div>
      </header>

      {userTagSlugs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-th-fg/50">Filter by tag:</span>
          {userTagSlugs.map((s) => (
            <TagChip key={s} slug={s} active={activeTags.includes(s)} onClick={() => toggle(s, activeTags, setActiveTags)} />
          ))}
          {activeTags.length > 0 && (
            <button onClick={() => setActiveTags([])} className="ml-1 text-xs text-th-fg/40 hover:text-th-accent">clear</button>
          )}
        </div>
      )}

      {catalog.isLoading ? (
        <div className="py-12 text-center text-sm text-th-fg/50">Loading catalog…</div>
      ) : catalog.error ? (
        <div className="glass-section text-center">
          <h2 className="font-heading text-xl">Couldn't load the catalog</h2>
          <p className="mt-2 text-sm text-th-fg/60">{catalog.error.serverMessage ?? "Unknown error."}</p>
          <button className="btn mt-4" onClick={() => catalog.refetch()}>Retry</button>
        </div>
      ) : empty ? (
        <EmptyState hasTags={userTagSlugs.length > 0} />
      ) : view === "grid" ? (
        <GridView items={items} />
      ) : (
        <ListView items={items} />
      )}

      {catalog.hasNextPage && (
        <div className="pt-4 text-center">
          <button className="btn" onClick={() => catalog.fetchNextPage()} disabled={catalog.isFetchingNextPage}>
            {catalog.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  )
}

function toggle(s: string, current: string[], set: (next: string[]) => void) {
  set(current.includes(s) ? current.filter((x) => x !== s) : [...current, s])
}

function GridView({ items }: { items: VideoSummary[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((v) => (
        <Link key={v.id} to={`/v/${v.slug}`} className="glass-card-hover group flex flex-col gap-3 p-0 overflow-hidden">
          <Thumb video={v} />
          <div className="flex flex-1 flex-col gap-1.5 p-4 pt-2">
            <h3 className="font-heading text-base font-semibold leading-tight text-th-fg group-hover:text-th-accent transition-colors">
              {v.title}
            </h3>
            <div className="text-xs text-th-fg/50">{formatDate(v.performed_on)}</div>
            <div className="mt-auto flex flex-wrap gap-1 pt-2">
              {v.tag_slugs.slice(0, 3).map((s) => <TagChip key={s} slug={s} />)}
              {v.tag_slugs.length > 3 && (
                <span className="tag-badge">+{v.tag_slugs.length - 3}</span>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}

function ListView({ items }: { items: VideoSummary[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-th-glass/[0.08]">
      <table className="w-full text-sm">
        <thead className="bg-th-glass/[0.04] text-left text-xs uppercase tracking-wide text-th-fg/50">
          <tr>
            <th className="px-4 py-2">Title</th>
            <th className="px-4 py-2">Performed</th>
            <th className="px-4 py-2">Length</th>
            <th className="px-4 py-2">Tags</th>
          </tr>
        </thead>
        <tbody>
          {items.map((v, i) => (
            <tr key={v.id} className={clsx(i % 2 === 0 ? "bg-transparent" : "bg-th-glass/[0.02]", "hover:bg-th-glass/[0.06] transition-colors")}>
              <td className="px-4 py-2.5">
                <Link to={`/v/${v.slug}`} className="font-medium text-th-fg hover:text-th-accent">
                  {v.title}
                </Link>
                {v.description && <div className="text-xs text-th-fg/40 line-clamp-1">{v.description}</div>}
              </td>
              <td className="px-4 py-2.5 text-th-fg/60">{formatDate(v.performed_on)}</td>
              <td className="px-4 py-2.5 text-th-fg/60">{formatDuration(v.duration_seconds)}</td>
              <td className="px-4 py-2.5">
                <div className="flex flex-wrap gap-1">
                  {v.tag_slugs.map((s) => <TagChip key={s} slug={s} />)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Thumb({ video }: { video: VideoSummary }) {
  return (
    <div className="relative aspect-video w-full overflow-hidden bg-th-glass/[0.04]">
      {video.thumbnail_url ? (
        <img src={video.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-th-fg/20">
          <PlayCircle size={48} />
        </div>
      )}
      {video.duration_seconds && (
        <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {formatDuration(video.duration_seconds)}
        </span>
      )}
    </div>
  )
}

function EmptyState({ hasTags }: { hasTags: boolean }) {
  return (
    <div className="glass-section text-center">
      <h2 className="font-heading text-xl">No concerts yet</h2>
      <p className="mt-2 text-sm text-th-fg/60">
        {hasTags
          ? "Nothing matches your tags right now. Check back as the archive grows."
          : "You don't have any tags yet, so the catalog is empty for you."}
      </p>
      {!hasTags && (
        <Link to="/request-access" className="btn-primary mt-6 inline-flex">
          Request access
        </Link>
      )}
    </div>
  )
}
